import { ViewportScroller } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { NavigationStart, Router, Scroll } from '@angular/router';

import { KeyboardEnum } from '~/component/enums/keyboard.enum';
import { generateUid } from '~/core/helpers/uid/uid.helper';

import { TabBarLinkInterface } from './interfaces/tab-link.interface';

@Component({
  selector: 'app-router-tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss'],
})
export class RouterTabBarComponent implements OnInit {
  @Input() public tabBarLinks: TabBarLinkInterface[] = [];

  @ViewChildren('tabs') public tabs: QueryList<ElementRef>;

  private readonly uid = `tab-${generateUid()}`;

  public readonly tabpanelId = `tabpanel-${generateUid()}`;

  private scrollY: number;

  constructor(
    @Inject('Window') private readonly window: Window,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller
  ) {}

  public ngOnInit(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.scrollY = this.window.scrollY;
      }

      if (event instanceof Scroll && !(event.position || event.anchor)) {
        this.viewportScroller.scrollToPosition([0, this.scrollY]);
      }
    });
  }

  public get ariaLabelledBy(): string {
    return this.tabs?.find((tab) =>
      tab.nativeElement.parentElement.classList.contains(
        'tab-bar__item--active'
      )
    )?.nativeElement.id;
  }

  private focusTab(tabIndex: number): void {
    this.tabs
      .toArray()
      .find((_tab, index) => tabIndex === index)
      .nativeElement.focus();
  }

  public getTabId(index: number): string {
    return `${this.uid}-${index + 1}`;
  }

  public onKeydown(event: KeyboardEvent): void {
    const maxIndex = this.tabs.length - 1;
    const currentIndex = this.tabs
      .toArray()
      .findIndex((tab) => tab.nativeElement === event.target);

    if (event.code === KeyboardEnum.Space) {
      event.preventDefault();

      const target = event.target as HTMLAnchorElement;

      target.click();
    }

    if (event.code === KeyboardEnum.ArrowLeft) {
      if (currentIndex === 0) {
        this.focusTab(maxIndex);
      }

      this.focusTab(currentIndex - 1);
    }

    if (event.code === KeyboardEnum.ArrowRight) {
      if (currentIndex === maxIndex) {
        this.focusTab(0);
      }

      this.focusTab(currentIndex + 1);
    }

    if (event.code === KeyboardEnum.Home) {
      event.preventDefault();
      this.focusTab(0);
    }

    if (event.code === KeyboardEnum.End) {
      event.preventDefault();
      this.focusTab(maxIndex);
    }
  }
}
